import { useMemo } from 'react';
import type { TemplateDef, FieldDef } from '../types';
import {
  sellerOptions,
  industryOptions,
} from './mockOptions';
import { useDistributors } from '../../../../../app/modules/auth';
import {
  buildBasicInfoSection,
  buildFinancialSection,
  buildValiditySection,
  buildTargetingSection,
  cardNumberField,
  discountValueField,
  discountTypeField,
  familyIndustryField,
  mandatoryEANsField,
  mandatorySkusField,
  skuComboField,
  itemTypeIncludedField,
  itemsIncludedField,
  discountCriteriaField,
  minQtyField,
  maxQtyField,
  minValueField,
  maxValueField,
  cnpjsIncludedField,
  cnpjsExceptionField,
  defaultIndustryOptions,
} from './commonFields';

const familyTypeField: FieldDef = {
  key: 'tipoFamilia',
  label: 'Tipo de família',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — Requisito (obrigatória)' },
    { value: '2', label: '2 — Benefício' },
    { value: '3', label: '3 — Ambos' },
  ],
  unique: true,
};

const familyNameField: FieldDef = {
  key: 'nomeFamilia',
  label: 'Nome da família',
  type: 'text',
  required: true,
  placeholder: 'ex: Shampoo / Condicionador',
  helperText: 'Nome sugerido para identificar o agrupamento.',
  unique: true,
};

const familyObsField: FieldDef = {
  key: 'obsFamilia',
  label: 'OBS Família existente',
  type: 'text',
  placeholder: 'ex: Já usei essa família na promo XYZ',
  helperText: 'Se reutilizando família de outra promoção, citar aqui o nome.',
  unique: true,
};

const familySkusField: FieldDef = {
  key: 'skusFamilia',
  label: 'SKUs da família',
  type: 'textarea',
  rows: 3,
  placeholder: 'Lista de SKUs (EANs + caixarias) separados por vírgula',
  helperText: 'Lista de SKUs (EANs + caixarias) separados por vírgula.',
  unique: true,
  bulkImport: true,
};

const distinctEANsField: FieldDef = {
  key: 'qtdEANsDistintos',
  label: 'Quantidade de EANs distintos para ativação da promo',
  type: 'number',
  placeholder: 'ex: 1',
  helperText: 'Qtd de EANs diferentes necessários para ativar.',
  unique: true,
};

const minActivationQtyField: FieldDef = {
  key: 'qtdMinimaAtivacao',
  label: 'Quantidade mínima para ativar a promo',
  type: 'number',
  placeholder: 'ex: 1',
  unique: true,
};

const maxActivationQtyField: FieldDef = {
  key: 'qtdMaximaAtivacao',
  label: 'Quantidade máxima para ativar a promo',
  type: 'number',
  placeholder: 'Vazio = sem máximo',
  helperText: 'Deixe vazio quando não houver quantidade máxima.',
  unique: true,
};

const familyCountField: FieldDef = {
  key: 'qtdFamiliasAtivacao',
  label: 'Quantidade de famílias para ativar a promo',
  type: 'number',
  required: true,
  placeholder: 'ex: 3',
  helperText: 'Deve ser ≥ total de famílias obrigatórias e ≤ total de famílias na promo.',
  unique: true,
};

const mandatoryFamilyField: FieldDef = {
  key: 'familiaObrigatoria',
  label: 'Família obrigatória para ativação?',
  type: 'select',
  options: [
    { value: 'SIM', label: 'SIM' },
    { value: 'NÃO', label: 'NÃO' },
  ],
  unique: true,
};

const listTypeField: FieldDef = {
  key: 'tipoLista',
  label: 'Tipo de Lista',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — Original' },
    { value: '2', label: '2 — Recompra' },
  ],
  unique: true,
};

const originalListTypeField: FieldDef = {
  ...listTypeField,
  options: [{ value: '1', label: '1 — Original' }],
  helperText: 'Fixo neste template.',
  disabled: true,
};

const repurchaseListTypeField: FieldDef = {
  ...listTypeField,
  options: [{ value: '2', label: '2 — Recompra' }],
  helperText: 'Fixo neste template.',
  disabled: true,
};

const repurchaseAvailableField: FieldDef = {
  key: 'recompraDisponivel',
  label: 'Recompra disponível',
  type: 'select',
  required: true,
  options: [
    { value: 'Yes', label: 'Sim' },
    { value: 'No', label: 'Não' },
  ],
  helperText: 'A lista original pode gerar recompra?',
  unique: true,
};

const familyPositionField: FieldDef = {
  key: 'posicaoFamilia',
  label: 'Posição da família dentro da lista',
  type: 'number',
  required: true,
  placeholder: 'ex: 1',
  helperText: 'Boa prática: família gatilho = 1, demais = 2, 3...',
  unique: true,
};

const familyDiscountField: FieldDef = {
  key: 'descontoFamilia',
  label: 'Desconto da família (%)',
  type: 'number',
  placeholder: 'ex: 6',
  helperText: 'Percentual de desconto da família.',
  unique: true,
};

const listPositionField: FieldDef = {
  key: 'posicaoLista',
  label: 'Posição da lista dentro da seção',
  type: 'number',
  required: true,
  placeholder: 'ex: 1',
  helperText: 'Para UL: Lista G=1, Lista M=2, Lista P=3, Lista PP=4. Em empate: ordem alfabética.',
  unique: true,
};

const listSectionField: FieldDef = {
  key: 'secaoLista',
  label: 'Seção na qual a lista deverá aparecer',
  type: 'text',
  required: true,
  placeholder: 'ex: Beleza e bem estar',
  helperText: 'Para UL: Cuidados pessoais, Cuidados com a Casa, Beleza e bem estar, Nutrição... Para Multi: nome da indústria.',
  unique: true,
};

const refListIdField: FieldDef = {
  key: 'idListaOriginal',
  label: 'ID da lista sugerida de referência para recompra',
  type: 'text',
  required: true,
  placeholder: 'ex: 123456789',
  helperText: 'ID da lista original que origina esta recompra.',
  unique: true,
};

const couponCodeField: FieldDef = {
  key: 'codigoCupom',
  label: 'Código do cupom',
  type: 'text',
  required: true,
  placeholder: 'ex: QUERIDINHOSBW',
  helperText: 'Máx. 60 caracteres. Sem espaços. Aceita hífen (-) e underline (_). Deve ser único e nunca usado antes.',
  unique: true,
};

const couponQtyField: FieldDef = {
  key: 'qtdCupons',
  label: 'Quantidade de cupons disponíveis',
  type: 'number',
  placeholder: 'Vazio = ilimitado',
  helperText: 'Total de resgates possíveis. Deixe vazio quando for ilimitado.',
  unique: true,
};

const couponUsesPerCustomerField: FieldDef = {
  key: 'usosPorCliente',
  label: 'Quantidade de usos por cliente',
  type: 'number',
  required: true,
  placeholder: 'ex: 1',
  helperText: 'Máximo de usos por CNPJ. 99999 = ilimitado.',
  unique: true,
};

const itemTypeExcludedField: FieldDef = {
  key: 'tipoItemExcluido',
  label: 'Tipo de item a ser EXCLUÍDO na promoção',
  type: 'select',
  options: [
    { value: '1', label: '1 — EANs' },
    { value: '2', label: '2 — SKUs' },
    { value: '3', label: '3 — Marcas' },
    { value: '4', label: '4 — Categorias' },
    { value: '5', label: '5 — Subcategorias' },
    { value: '6', label: '6 — Departamentos' },
    { value: '7', label: '7 — Indústrias' },
    { value: '8', label: '8 — Famílias' },
  ],
  unique: true,
};

const itemsExcludedField: FieldDef = {
  key: 'itensExcluidos',
  label: 'Itens a serem EXCLUÍDOS da promoção',
  type: 'textarea',
  rows: 3,
  placeholder: 'Conforme o tipo escolhido, separados por vírgula',
  unique: true,
};

const hasCouponField: FieldDef = {
  key: 'cupom',
  label: 'Cupom',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — Sem cupom (Rebaixa)' },
    { value: '2', label: '2 — Com cupom' },
  ],
  helperText: 'Define se há cupom ou não nesta promoção.',
  unique: true,
};

const eansInclusionField: FieldDef = { key: 'eansInclusao', label: 'EANs inclusão', type: 'tags', excludeValuesFrom: 'eansExclusao', placeholder: 'Digite ou cole EANs', helperText: 'EANs a serem incluídos na promoção.', unique: true };
const eansExclusionField: FieldDef = { key: 'eansExclusao', label: 'EANs exclusão', type: 'tags', excludeValuesFrom: 'eansInclusao', placeholder: 'Digite ou cole EANs', helperText: 'EANs a serem excluídos da promoção.', unique: true };
const skusInclusionField: FieldDef = { key: 'skusInclusao', label: 'SKUs inclusão', type: 'tags', excludeValuesFrom: 'skusExclusao', placeholder: 'Digite ou cole SKUs', helperText: 'SKUs (com caixarias) a serem incluídos.', unique: true };
const skusExclusionField: FieldDef = { key: 'skusExclusao', label: 'SKUs exclusão', type: 'tags', excludeValuesFrom: 'skusInclusao', placeholder: 'Digite ou cole SKUs', unique: true };
const brandsInclusionField: FieldDef = { key: 'marcasInclusao', label: 'Marcas inclusão', type: 'tags', excludeValuesFrom: 'marcasExclusao', placeholder: 'Digite ou cole marcas', unique: true };
const brandsExclusionField: FieldDef = { key: 'marcasExclusao', label: 'Marcas exclusão', type: 'tags', excludeValuesFrom: 'marcasInclusao', placeholder: 'Digite ou cole marcas', unique: true };
const categoriesInclusionField: FieldDef = { key: 'categoriasInclusao', label: 'Categorias inclusão', type: 'tags', excludeValuesFrom: 'categoriasExclusao', placeholder: 'Digite ou cole categorias', unique: true };
const categoriesExclusionField: FieldDef = { key: 'categoriasExclusao', label: 'Categorias exclusão', type: 'tags', excludeValuesFrom: 'categoriasInclusao', placeholder: 'Digite ou cole categorias', unique: true };
const subcategoriesInclusionField: FieldDef = { key: 'subcategoriasInclusao', label: 'Subcategorias inclusão', type: 'tags', excludeValuesFrom: 'subcategoriasExclusao', placeholder: 'Digite ou cole subcategorias', unique: true };
const subcategoriesExclusionField: FieldDef = { key: 'subcategoriasExclusao', label: 'Subcategorias exclusão', type: 'tags', excludeValuesFrom: 'subcategoriasInclusao', placeholder: 'Digite ou cole subcategorias', unique: true };
const departmentsInclusionField: FieldDef = { key: 'departamentosInclusao', label: 'Departamentos inclusão', type: 'tags', excludeValuesFrom: 'departamentosExclusao', placeholder: 'Digite ou cole departamentos', unique: true };
const departmentsExclusionField: FieldDef = { key: 'departamentosExclusao', label: 'Departamentos exclusão', type: 'tags', excludeValuesFrom: 'departamentosInclusao', placeholder: 'Digite ou cole departamentos', unique: true };
const industriesInclusionField: FieldDef = { key: 'industriasInclusao', label: 'Indústrias inclusão', type: 'tags', options: industryOptions, excludeValuesFrom: 'industriasExclusao', placeholder: 'Selecione ou digite indústrias', unique: true, bulkImport: true };
const industriesExclusionField: FieldDef = { key: 'industriasExclusao', label: 'Indústrias exclusão', type: 'tags', options: industryOptions, excludeValuesFrom: 'industriasInclusao', placeholder: 'Selecione ou digite indústrias', unique: true, bulkImport: true };
const familiesInclusionField: FieldDef = { key: 'familiasInclusao', label: 'Famílias inclusão', type: 'tags', excludeValuesFrom: 'familiasExclusao', placeholder: 'Digite ou cole famílias', unique: true };
const familiesExclusionField: FieldDef = { key: 'familiasExclusao', label: 'Famílias exclusão', type: 'tags', excludeValuesFrom: 'familiasInclusao', placeholder: 'Digite ou cole famílias', unique: true };

export const templates: TemplateDef[] = [
  {
    id: 't1',
    number: '01',
    name: 'Rebaixa Simples',
    description: 'Desconto direto no item (%, R$ fixo ou % no carrinho) acionado por quantidade ou valor mínimo de compra.',
    mechanicsBadge: 'Tipo de mecânica = 1',
    color: '#1976d2',
    sections: [
      buildBasicInfoSection('1 — Rebaixa Simples'),
      buildFinancialSection(true, true),
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Critério de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, maxValueField],
      },
      {
        title: 'Produtos',
        fields: [itemTypeIncludedField, itemsIncludedField, mandatoryEANsField, mandatorySkusField, skuComboField, familyIndustryField],
      },
    ],
  },
  {
    id: 't2',
    number: '02',
    name: 'Rebaixa Progressiva',
    description: 'Múltiplas faixas de desconto definidas por cards numerados sequencialmente. Cada faixa tem sua própria faixa de valor/quantidade.',
    mechanicsBadge: 'Tipo de mecânica = 2',
    color: '#388e3c',
    sections: [
      buildBasicInfoSection('2 — Rebaixa Progressiva'),
      {
        title: 'Cards e Desconto',
        fields: [cardNumberField, discountValueField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text', placeholder: 'Apenas no card pai', helperText: 'Informar apenas no card pai (repetir nos filhos se necessário).' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Faixa de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, { ...maxValueField, helperText: 'Usar valor muito alto (ex: 999999) para ilimitado.' }],
      },
      {
        title: 'Produtos',
        fields: [
          { ...itemTypeIncludedField, helperText: 'Informar apenas no card pai (card 1).' },
          { ...itemsIncludedField, helperText: 'Conforme tipo escolhido. Informar apenas no card pai.' },
          mandatoryEANsField,
          mandatorySkusField,
          skuComboField,
          familyIndustryField,
        ],
      },
    ],
  },
  {
    id: 't3',
    number: '03',
    name: 'Compre e Ganhe',
    description: 'Mecânica A+B baseada em famílias de produtos. Cada linha representa uma família que pode ser Requisito, Benefício ou Ambos.',
    mechanicsBadge: 'Tipo de mecânica = 3',
    color: '#f57c00',
    sections: [
      buildBasicInfoSection('3 — Compre e Ganhe'),
      {
        title: 'Cards e Desconto',
        fields: [
          cardNumberField,
          {
            ...discountValueField,
            label: 'Valor do desconto (%)',
            required: true,
            conditional: { field: 'tipoFamilia', values: ['2', '3'] },
            helperText: 'Informe o percentual, sem o símbolo %. Deve ser igual nas famílias Benefício e Ambos da promoção.',
          },
        ],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text', placeholder: 'Listar no card pai' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Gatilho da Família',
        fields: [
          { ...minValueField, helperText: 'Valor mínimo do carrinho para ativar a promoção (opcional).', conditional: undefined },
          { ...maxValueField, helperText: 'Valor máximo do carrinho (opcional).', conditional: undefined },
        ],
      },
      {
        title: 'Configuração da Família',
        fields: [
          familyTypeField,
          familyNameField,
          familyIndustryField,
          familyObsField,
          familySkusField,
          { ...distinctEANsField, required: true, conditional: { field: 'tipoFamilia', values: ['1', '3'] } },
          { ...minActivationQtyField, required: true, conditional: { field: 'tipoFamilia', values: ['1', '3'] } },
          maxActivationQtyField,
          familyCountField,
          { ...mandatoryFamilyField, required: true, conditional: { field: 'tipoFamilia', values: ['1', '3'] } },
        ],
      },
    ],
  },
  {
    id: 't4a',
    number: '04a',
    name: 'Lista Sugerida — Original',
    description: 'Lista curada de produtos para exibição no site/app. Define posição, desconto por família e seção.',
    mechanicsBadge: 'Tipo de mecânica = 4 · Tipo de Lista = 1',
    color: '#7b1fa2',
    sections: [
      {
        title: 'Informações Básicas',
        fields: [
          { key: 'tipoSolicitacao', label: 'Tipo de solicitação', type: 'select', options: [{ value: '0', label: '0 — Promoção nova' }, { value: '1', label: '1 — Legado' }] },
          { key: 'idIFC', label: 'ID Infracommerce', type: 'text', placeholder: 'ex: 123 ou n/a' },
          { key: 'tipoMecanica', label: 'Tipo de mecânica', type: 'info', helperText: '4 — Lista sugerida' },
          originalListTypeField,
          repurchaseAvailableField,
          { key: 'nomePromo', label: 'Nome da promo / cupom', type: 'text', required: true },
          { key: 'nomeExibicao', label: 'Nome de exibição da promoção', type: 'text', required: true },
          { key: 'descricao', label: 'Descrição da promoção', type: 'textarea', required: true, rows: 3 },
          { key: 'descricaoInterna', label: 'Descrição interna de campanha', type: 'textarea', rows: 2 },
        ],
      },
      {
        title: 'Cards e Estrutura',
        fields: [cardNumberField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text', placeholder: 'ex: Unilever 100%' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Configuração da Família',
        fields: [
          { ...familyNameField, required: true },
          { ...familyIndustryField, required: true },
          familyObsField,
          familySkusField,
          { ...distinctEANsField, required: true },
          maxActivationQtyField,
          { ...familyCountField },
          { ...mandatoryFamilyField, required: true },
          { ...familyPositionField, required: true },
          familyDiscountField,
          { ...listPositionField, required: true },
          { ...listSectionField, required: true },
        ],
      },
    ],
  },
  {
    id: 't4b',
    number: '04b',
    name: 'Lista Sugerida — Recompra',
    description: 'Variante da lista para clientes que já compraram da lista original. A segmentação usa o ID da lista original como referência.',
    mechanicsBadge: 'Tipo de mecânica = 4 · Tipo de Lista = 2',
    color: '#ad1457',
    sections: [
      {
        title: 'Informações Básicas',
        fields: [
          { key: 'tipoSolicitacao', label: 'Tipo de solicitação', type: 'select', options: [{ value: '0', label: '0 — Promoção nova' }, { value: '1', label: '1 — Legado' }] },
          { key: 'idIFC', label: 'ID Infracommerce', type: 'text', placeholder: 'ex: 123 ou n/a' },
          { key: 'tipoMecanica', label: 'Tipo de mecânica', type: 'info', helperText: '4 — Lista sugerida' },
          repurchaseListTypeField,
          refListIdField,
          { key: 'nomePromo', label: 'Nome da promo / cupom', type: 'text', required: true },
          { key: 'nomeExibicao', label: 'Nome de exibição da promoção', type: 'text', required: true },
          { key: 'descricao', label: 'Descrição da promoção', type: 'textarea', required: true, rows: 3 },
          { key: 'descricaoInterna', label: 'Descrição interna de campanha', type: 'textarea', rows: 2 },
        ],
      },
      {
        title: 'Cards e Estrutura',
        fields: [cardNumberField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      {
        title: 'Segmentação',
        fields: [
          { key: 'sellersIncluidos', label: 'Sellers incluídos', type: 'multiselect', options: sellerOptions, excludeValuesFrom: 'sellersExcecao', helperText: 'Selecione os sellers que terão acesso à promoção.', bulkImport: true },
          { key: 'sellersExcecao', label: 'Sellers exceção', type: 'multiselect', options: sellerOptions, excludeValuesFrom: 'sellersIncluidos', helperText: 'Sellers excluídos da promoção (exceção).', bulkImport: true },
          { ...cnpjsIncludedField, label: 'CNPJs incluídos — ID da lista original', required: true, helperText: '⚠️ Campo com semântica diferente: aqui recebe o ID ou nome da segmentação da lista original.' },
          cnpjsExceptionField,
          { key: 'canais', label: 'Canais de veiculação de promoção', type: 'multiselect', required: true, options: [{ value: '1', label: '1 — Site' }, { value: '2', label: '2 — App' }, { value: '3', label: '3 — WhatsApp' }] },
          { key: 'tipoPedido', label: 'Tipo de pedido', type: 'multiselect', required: true, options: [{ value: '1', label: '1 — Autônomo' }, { value: '2', label: '2 — Venda assistida' }] },
        ],
      },
      {
        title: 'Configuração da Família',
        fields: [
          familyNameField,
          familyIndustryField,
          familyObsField,
          familySkusField,
          distinctEANsField,
          maxActivationQtyField,
          { ...familyCountField, required: false },
          mandatoryFamilyField,
          familyPositionField,
          familyDiscountField,
          listPositionField,
          listSectionField,
        ],
      },
    ],
  },
  {
    id: 't5',
    number: '05',
    name: 'Cupom Simples',
    description: 'Equivale à Rebaixa Simples com adição de código de cupom, controle de quantidade total e usos por cliente.',
    mechanicsBadge: 'Tipo de mecânica = 5',
    color: '#00796b',
    sections: [
      buildBasicInfoSection('5 — Cupom Simples'),
      {
        title: 'Desconto',
        fields: [discountTypeField, discountValueField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Critério de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, maxValueField],
      },
      {
        title: 'Cupom',
        fields: [couponCodeField, couponQtyField, couponUsesPerCustomerField],
      },
      {
        title: 'Produtos',
        fields: [itemTypeIncludedField, itemsIncludedField, mandatoryEANsField, mandatorySkusField, skuComboField, familyIndustryField],
      },
    ],
  },
  {
    id: 't6',
    number: '06',
    name: 'Cupom Progressivo',
    description: 'Cupom com múltiplas faixas de valor/quantidade. Adiciona campos de exclusão de itens em relação ao Cupom Simples.',
    mechanicsBadge: 'Tipo de mecânica = 6',
    color: '#5d4037',
    sections: [
      buildBasicInfoSection('6 — Cupom Progressivo'),
      {
        title: 'Cards e Desconto',
        fields: [cardNumberField, discountTypeField, discountValueField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Faixa de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, maxValueField],
      },
      {
        title: 'Cupom',
        fields: [couponCodeField, couponQtyField, couponUsesPerCustomerField],
      },
      {
        title: 'Produtos',
        fields: [
          itemTypeIncludedField,
          itemsIncludedField,
          itemTypeExcludedField,
          itemsExcludedField,
          mandatoryEANsField,
          mandatorySkusField,
          skuComboField,
        ],
      },
    ],
  },
  {
    id: 't7',
    number: '07',
    name: 'Cashback',
    description: 'Similar ao Cupom Simples. O valor do desconto representa o cashback (R$ fixo no carrinho ou %). Tem campos de exclusão de itens.',
    mechanicsBadge: 'Tipo de mecânica = 7',
    color: '#c62828',
    sections: [
      buildBasicInfoSection('7 — Cashback'),
      {
        title: 'Valor do Cashback',
        fields: [{ ...discountValueField, helperText: 'Valor do cashback. Sem "R$" ou "%". Pode ser valor fixo no carrinho.' }],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Critério de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, maxValueField],
      },
      {
        title: 'Cupom',
        fields: [couponCodeField, couponQtyField, couponUsesPerCustomerField],
      },
      {
        title: 'Produtos',
        fields: [
          itemTypeIncludedField,
          itemsIncludedField,
          itemTypeExcludedField,
          itemsExcludedField,
          mandatoryEANsField,
          mandatorySkusField,
          skuComboField,
        ],
      },
    ],
  },
  {
    id: 't8',
    number: '08',
    name: 'Rebaixa / Cupom Geral',
    description: 'Template mais completo e flexível. Suporta Rebaixa ou Cupom num único formulário. 55 campos com inclusão/exclusão granular por tipo de item.',
    mechanicsBadge: 'Tipo de mecânica = 1 ou 5 · Campo "Cupom": 1=Sem / 2=Com',
    color: '#37474f',
    sections: [
      buildBasicInfoSection('1 (Rebaixa) ou 5 (Cupom)'),
      {
        title: 'Cards e Desconto',
        fields: [cardNumberField, discountTypeField, discountValueField],
      },
      {
        title: 'Patrocínio',
        fields: [
          { key: 'industria', label: 'Indústria', type: 'select', required: true, options: [{ value: '1', label: '1 — Unilever' }, { value: '2', label: '2 — Multi' }, { value: '3', label: '3 — E-UB' }] },
          { key: 'multiPatrocinador', label: 'Multi patrocinador', type: 'select', required: true, options: [{ value: '0', label: '0 — Não' }, { value: '1', label: '1 — Sim' }] },
          { key: 'patrocinadores', label: 'Patrocinador(es) e contribuição', type: 'text' },
          { key: 'cumulativa', label: 'Promoção cumulativa?', type: 'select', required: true, options: [{ value: '1', label: '1 — Sim' }, { value: '2', label: '2 — Não' }] },
        ],
      },
      buildValiditySection(),
      buildTargetingSection(),
      {
        title: 'Critério de Desconto',
        fields: [discountCriteriaField, minQtyField, maxQtyField, minValueField, maxValueField],
      },
      {
        title: 'Cupom',
        fields: [
          hasCouponField,
          { ...couponCodeField, conditional: { field: 'cupom', values: ['2'] }, helperText: 'Preencher apenas se Cupom = 2. Max 60 chars, sem espaços.' },
          { ...couponQtyField, conditional: { field: 'cupom', values: ['2'] }, helperText: 'Apenas se Cupom = 2. Deixe vazio quando for ilimitado.' },
          { ...couponUsesPerCustomerField, conditional: { field: 'cupom', values: ['2'] }, helperText: 'Apenas se Cupom = 2. 99999 = ilimitado.' },
        ],
      },
      {
        title: 'Inclusão e Exclusão de Produtos',
        fields: [
          eansInclusionField,
          eansExclusionField,
          skusInclusionField,
          skusExclusionField,
          brandsInclusionField,
          brandsExclusionField,
          categoriesInclusionField,
          categoriesExclusionField,
          subcategoriesInclusionField,
          subcategoriesExclusionField,
          departmentsInclusionField,
          departmentsExclusionField,
          industriesInclusionField,
          industriesExclusionField,
          familiesInclusionField,
          familiesExclusionField,
        ],
      },
      {
        title: 'Itens Obrigatórios',
        fields: [mandatoryEANsField, mandatorySkusField, skuComboField],
      },
    ],
  },
];


type HookPayload = unknown;
type FieldOption = NonNullable<FieldDef['options']>[number];

const getArrayPayload = (payload: HookPayload, arrayKeys: string[]): unknown[] => {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    for (const key of arrayKeys) {
      if (Array.isArray(record[key])) return record[key] as unknown[];
    }

    if (record.data && typeof record.data === 'object') {
      const dataRecord = record.data as Record<string, unknown>;

      for (const key of arrayKeys) {
        if (Array.isArray(dataRecord[key])) return dataRecord[key] as unknown[];
      }
    }
  }

  return [];
};

const normalizeOptions = (
  payload: HookPayload,
  fallbackOptions: NonNullable<FieldDef['options']>,
  arrayKeys: string[],
  valueKeys: string[],
  labelKeys: string[]
): NonNullable<FieldDef['options']> => {
  const options = getArrayPayload(payload, arrayKeys)
    .map((item): FieldOption | null => {
      if (item == null) return null;

      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item);
        return { value, label: value };
      }

      if (typeof item !== 'object') return null;

      const record = item as Record<string, unknown>;

      const rawValue = valueKeys.map((key) => record[key]).find((value) => value != null);
      const rawLabel = labelKeys.map((key) => record[key]).find((value) => value != null);

      const valueSource = rawValue ?? rawLabel;
      const labelSource = rawLabel ?? rawValue;

      if (valueSource == null || labelSource == null) return null;

      const value = String(valueSource);
      const label = rawValue != null && rawLabel != null ? `${String(rawValue)} — ${String(rawLabel)}` : String(labelSource);

      return { value, label };
    })
    .filter((option): option is FieldOption => Boolean(option));

  return options.length > 0 ? options : fallbackOptions;
};

export const normalizeIndustryOptions = (payload: HookPayload): NonNullable<FieldDef['options']> =>
  normalizeOptions(
    payload,
    defaultIndustryOptions,
    ['data', 'items', 'industries', 'result'],
    ['value', 'id', 'IDIndustria', 'idIndustria', 'industryId', 'codigo', 'Codigo'],
    ['label', 'Industria', 'industria', 'NomeIndustria', 'nomeIndustria', 'Nome', 'nome', 'name']
  );

export const normalizeDistributorOptions = (payload: HookPayload): NonNullable<FieldDef['options']> =>
  normalizeOptions(
    payload,
    sellerOptions,
    ['data', 'items', 'distributors', 'distribuidores', 'result'],
    ['value', 'id', 'IDDistribuidor', 'idDistribuidor', 'distributorId', 'codigo', 'Codigo'],
    ['label', 'Distribuidor', 'distribuidor', 'NomeCurtoDistribuidor', 'NomeDistribuidor', 'nomeDistribuidor', 'Nome', 'nome', 'name']
  );

const bulkImportFieldKeys = new Set([
  'industria',
  'sellersIncluidos',
  'sellersExcecao',
  'cnpjsIncluidos',
  'cnpjsExcecao',
  'industriasInclusao',
  'industriasExclusao',
  'skusFamilia',
]);

const withBulkImport = (field: FieldDef): FieldDef =>
  bulkImportFieldKeys.has(field.key) ? { ...field, bulkImport: true } : field;

const withDynamicOptions = (
  templateList: TemplateDef[],
  distributorOptions: NonNullable<FieldDef['options']>
): TemplateDef[] =>
  templateList.map((template) => ({
    ...template,
    sections: template.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        if (field.key === 'industria' || field.key === 'industriasInclusao' || field.key === 'industriasExclusao') {
          return withBulkImport({
            ...field,
            options: industryOptions,
          });
        }

        if (field.key === 'sellersIncluidos' || field.key === 'sellersExcecao') {
          return withBulkImport({
            ...field,
            options: distributorOptions,
          });
        }

        return withBulkImport(field);
      }),
    })),
  }));

export const useTemplates = (): TemplateDef[] => {
  const distributors = useDistributors();

  const distributorOptions = useMemo(
    () => normalizeDistributorOptions(distributors),
    [distributors]
  );

  return useMemo(
    () => withDynamicOptions(templates, distributorOptions),
    [distributorOptions]
  );
};
